import cloneDeep from 'lodash/cloneDeep'

import contentful, {
  flattenEntryResponse,
  flattenEntryResponseSingle,
} from '@aletheia/common/lib/contentful'

import { IPayoutReport } from '../types/generated'

/**
 * Payout reports have `body2` and `body3` fields, to account for the fact that
 * we sometimes run over the 50,000 character limit of a Contentful text field.
 *
 * This function concatenates these additional fields together into a single
 * `body` field
 */
function concatenateBodyFields(entry: undefined): undefined
function concatenateBodyFields(
  entry: IPayoutReport | undefined,
): IPayoutReport | undefined
function concatenateBodyFields(entry: IPayoutReport): IPayoutReport
function concatenateBodyFields(
  entry: IPayoutReport | undefined,
): IPayoutReport | undefined {
  if (!entry) return
  const _entry = cloneDeep(entry)
  for (const field of ['body2', 'body3'] as const) {
    const extraBody = _entry.fields[field]
    if (!extraBody) continue
    _entry.fields.body = [_entry.fields.body, extraBody].join('\n\n')
    delete _entry.fields[field]
  }
  return _entry
}

/**
 * Fetch a single Payout Report from Contenful
 *
 * @param {string} slug The slug of the Payout Report you wish to fetch
 */
export const fetchPayoutReport = async (
  slug: string,
): Promise<IPayoutReport | undefined> => {
  const payoutReport = (await flattenEntryResponseSingle(
    await contentful.getEntries({
      content_type: 'payoutReport',
      'fields.slug': slug,
      include: 5,
    }),
  )) as IPayoutReport | undefined
  return concatenateBodyFields(payoutReport)
}

/**
 * Fetch a single Payout Report from Contenful
 *
 * @param {string} id The id of the Payout Report you wish to fetch
 */
export const fetchPayoutReportById = async (
  id: string,
): Promise<IPayoutReport | undefined> => {
  const payoutReport = (await flattenEntryResponseSingle(
    await contentful.getEntries({
      content_type: 'payoutReport',
      'sys.id': id,
      include: 5,
    }),
  )) as IPayoutReport | undefined
  return concatenateBodyFields(payoutReport)
}

/**
 * Fetch all Payout Reports from Contentful
 *
 * @param {object} query Options to pass to contentful.getEntries()
 */
export const fetchPayoutReports = async (
  query?: Record<string, unknown>,
): Promise<IPayoutReport[]> => {
  const entries = (await flattenEntryResponse(
    await contentful.getEntries({
      include: 5,
      order: '-fields.date',
      ...query,
      content_type: 'payoutReport',
    }),
  )) as IPayoutReport[]
  return entries.map(concatenateBodyFields)
}

/**
 * Fetch all Payout Reports for a specific Fund from Contentful
 *
 * @param {object} query Options to pass to contentful.getEntries()
 */
export const fetchPayoutReportsByFund = async (
  fundSlug: string,
  query?: Record<string, unknown>,
): Promise<IPayoutReport[]> => {
  return flattenEntryResponse(
    await contentful.getEntries({
      include: 5,
      order: '-fields.date',
      ...query,
      'fields.fund.fields.slug': fundSlug,
      'fields.fund.sys.contentType.sys.id': 'fund',
      content_type: 'payoutReport',
    }),
  ) as IPayoutReport[]
}
